import { useEffect, useRef } from 'react';

type CopyTextFallbackModalProps = {
  open: boolean;
  title: string;
  text: string;
  onClose: () => void;
};

/** Shown when programmatic clipboard copy is blocked — user selects and copies manually. */
export function CopyTextFallbackModal({
  open,
  title,
  text,
  onClose,
}: CopyTextFallbackModalProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.select();
    el.setSelectionRange(0, text.length);
  }, [open, text]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="modal-panel copy-fallback-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="copy-fallback-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="copy-fallback-title">{title}</h2>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modal-body">
          <p className="text-muted copy-fallback-help">
            Automatic copy is blocked by your browser (common in embedded views). Select all and
            press <kbd>Ctrl+C</kbd> or <kbd>⌘C</kbd>.
          </p>
          <textarea
            ref={textareaRef}
            className="form-control copy-fallback-textarea"
            readOnly
            rows={8}
            value={text}
            aria-label="Text to copy"
          />
        </div>
      </div>
    </div>
  );
}
